<template>
    <div class="finance-vat-zero-export">
        <h4 class="mb-1">Выгрузка НДС 0</h4>
        <p class="text-muted small mb-4">Авиа-перевозки: Excel → XML для 1С → при необходимости объединение XML из 1С.</p>

        <div class="d-flex flex-wrap align-items-center mb-3">
            <div class="green-filter d-flex align-items-center mr-3">
                <span
                    v-for="(opt, index) in unitOptions"
                    :key="index"
                    :class="`green-link mr-2 ${unitFilter === opt.value ? 'active' : ''}`"
                    style="font-size: 12px; cursor: pointer;"
                    @click="unitFilter = opt.value"
                >{{ opt.title }}</span>
            </div>
            <div class="d-flex align-items-center ml-auto">
                <div class="input-group mr-2">
                    <input type="date" class="form-control start-input-date" v-model="period_start" style="max-width: 10rem;">
                    <input type="date" class="form-control finish-input-date" v-model="period_end" style="max-width: 10rem;">
                </div>
                <b-button variant="primary" @click="submitExcelExport" :disabled="loadingExcel">
                    <i class="fas fa-spinner fa-spin" v-if="loadingExcel"></i>
                    <span v-else>Сформировать Excel</span>
                </b-button>
            </div>
        </div>

        <div class="vat-zero-rules border rounded px-3 py-2 mb-3 bg-light">
            <div class="vat-zero-rules__title text-muted mb-1">Как отбираются перевозки в Excel</div>
            <template v-if="unitFilter === 'all'">
                <div class="mb-2">
                    <strong>B2B</strong>
                    <ul class="mb-0 pl-3" style="list-style: disc;">
                        <li>Только авиа-услуги, без признака B2C в карточке перевозки.</li>
                        <li>В отчёт попадают услуги, где есть <strong>входящие</strong> счета с <strong>нулевой ставкой НДС</strong> и без НДС в сумме, подразделение <strong>B2B</strong>.</li>
                        <li><strong>Дата реализации</strong> такого счёта должна попасть в выбранный период.</li>
                        <li>На <strong>каждый</strong> такой счёт - отдельная строка.</li>
                    </ul>
                </div>
                <div class="mb-0">
                    <strong>B2C</strong>
                    <ul class="mb-0 pl-3" style="list-style: disc;">
                        <li>Только авиа-услуги с признаком <strong>B2C</strong> в карточке перевозки.</li>
                        <li><strong>Дата выпуска AWB</strong> - в выбранном периоде.</li>
                        <li>По одной строке <strong>на каждого</strong> выбранного B2C-партнёра; ИНН, наименование и адрес грузополучателя - из карточки партнёра.</li>
                    </ul>
                </div>
            </template>
            <template v-else-if="unitFilter === 'B2B'">
                <ul class="mb-0 pl-3" style="list-style: disc;">
                    <li>Только авиа-услуги, без признака B2C в карточке перевозки.</li>
                    <li>Входящие счета НДС&nbsp;0, подразделение <strong>B2B</strong>, дата реализации в периоде.</li>
                </ul>
            </template>
            <template v-else>
                <ul class="mb-0 pl-3" style="list-style: disc;">
                    <li>Авиа-услуги с признаком <strong>B2C</strong>, дата AWB в периоде, строки по выбранным партнёрам.</li>
                </ul>
            </template>
        </div>

        <div v-if="exports.excel.path" class="vat-zero-download-wrap text-center mt-3 mb-4">
            <b-button
                variant="success"
                size="lg"
                class="vat-zero-download-btn py-3 mx-auto d-block"
                :disabled="downloadLoading.excel"
                @click="downloadExport('excel')"
            >
                <i class="fas fa-spinner fa-spin mr-2" v-if="downloadLoading.excel"></i>
                <i class="fas fa-file-excel mr-2" v-else></i>
                Скачать Excel
            </b-button>
        </div>

        <div class="vat-zero-convert border rounded px-3 py-3 bg-white mt-4">
            <div class="font-weight-bold mb-2">Шаг 2 - загрузка Excel для 1С</div>
            <p class="text-muted small mb-3">
                После проверки Excel (колонки U - налоговая база, V - примечание) нажмите кнопку ниже,
                выберите файл - он будет преобразован в XML.
            </p>
            <input
                ref="excelFileInput"
                type="file"
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                class="d-none"
                @change="onExcelFileSelected"
            >
            <div class="d-flex flex-wrap align-items-center">
                <b-button
                    variant="outline-primary"
                    class="mb-2"
                    :disabled="loadingXmlConvert"
                    @click="pickExcelFile"
                >
                    <i class="fas fa-spinner fa-spin mr-1" v-if="loadingXmlConvert"></i>
                    <i class="fas fa-file-code mr-1" v-else></i>
                    XML для 1С
                </b-button>
                <span v-if="excelUploadFilename" class="small text-muted ml-3 mb-2 text-truncate" style="max-width: 20rem;">
                    {{ excelUploadFilename }}
                </span>
            </div>
        </div>

        <div
            v-if="message.text && !(message.success && (exports.excel.path || exports.xml.path))"
            :class="`mt-3 small ${message.success ? 'text-success' : 'text-danger'}`"
        >
            {{ message.text }}
        </div>

        <div v-if="message.text && message.success && exports.xml.path" class="mt-3 small text-success text-center">
            {{ message.text }}
        </div>

        <div v-if="exports.xml.path" class="vat-zero-download-wrap text-center mt-3 mb-4">
            <b-button
                variant="success"
                size="lg"
                class="vat-zero-download-btn py-3 mx-auto d-block"
                :disabled="downloadLoading.xml"
                @click="downloadExport('xml')"
            >
                <i class="fas fa-spinner fa-spin mr-2" v-if="downloadLoading.xml"></i>
                <i class="fas fa-file-code mr-2" v-else></i>
                Скачать XML для 1С
            </b-button>
        </div>

        <div class="vat-zero-convert border rounded px-3 py-3 bg-white mt-4">
            <div class="font-weight-bold mb-2">Шаг 3 (необязательно) - объединение XML из 1С</div>
            <p class="text-muted small mb-3">
                Загрузите несколько XML, выгруженных из 1С. Отметьте файл, из которого взять заголовки
                (ИдФайл, Документ, РеестрАВН_9). Все записи <code>&lt;СведАВН&gt;</code> будут объединены,
                номера строк пересчитаны, итоговая налоговая база - сумма по всем строкам.
            </p>
            <input
                ref="xmlMergeFileInput"
                type="file"
                accept=".xml,application/xml,text/xml"
                multiple
                class="d-none"
                @change="onXmlMergeFilesSelected"
            >
            <div class="d-flex flex-wrap align-items-center mb-3">
                <b-button variant="outline-secondary" class="mb-2" @click="pickXmlMergeFiles">
                    <i class="fas fa-file-upload mr-1"></i>
                    Выбрать XML-файлы
                </b-button>
                <b-button
                    variant="primary"
                    class="ml-2 mb-2"
                    :disabled="xmlMergeFiles.length < 2 || loadingXmlMerge"
                    @click="mergeXmlFiles"
                >
                    <i class="fas fa-spinner fa-spin mr-1" v-if="loadingXmlMerge"></i>
                    <span v-else>Объединить в один XML</span>
                </b-button>
            </div>
            <div v-if="xmlMergeFiles.length > 0" class="vat-zero-merge-list small">
                <div
                    v-for="(file, index) in xmlMergeFiles"
                    :key="index"
                    class="d-flex align-items-center py-1 border-bottom"
                >
                    <input
                        type="radio"
                        class="mr-2"
                        name="xmlMergeHeaderSource"
                        :value="index"
                        v-model.number="xmlMergeHeaderSource"
                        :id="'xml-merge-src-' + index"
                    >
                    <label :for="'xml-merge-src-' + index" class="mb-0 flex-grow-1 text-truncate" style="cursor: pointer;">
                        <span class="text-muted mr-1">заголовки</span>{{ file.name }}
                    </label>
                </div>
                <div class="text-muted mt-1" style="font-size: 0.68rem;">
                    Отмеченный файл - источник заголовков итогового XML.
                </div>
            </div>
        </div>

        <div
            v-if="mergeMessage.text && !(mergeMessage.success && exports.mergedXml.path)"
            :class="`mt-3 small ${mergeMessage.success ? 'text-success' : 'text-danger'}`"
        >
            {{ mergeMessage.text }}
        </div>

        <div v-if="mergeMessage.text && mergeMessage.success && exports.mergedXml.path" class="mt-3 small text-success text-center">
            {{ mergeMessage.text }}
        </div>

        <div v-if="exports.mergedXml.path" class="vat-zero-download-wrap text-center mt-3">
            <b-button
                variant="success"
                size="lg"
                class="vat-zero-download-btn py-3 mx-auto d-block"
                :disabled="downloadLoading.mergedXml"
                @click="downloadExport('mergedXml')"
            >
                <i class="fas fa-spinner fa-spin mr-2" v-if="downloadLoading.mergedXml"></i>
                <i class="fas fa-layer-group mr-2" v-else></i>
                Скачать объединённый XML
            </b-button>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import {
    API_FINANCE_VAT_ZERO_EXPORT,
    API_FINANCE_VAT_ZERO_EXPORT_CONVERT_XML,
    API_FINANCE_VAT_ZERO_EXPORT_MERGE_XML,
    API_FINANCE_VAT_ZERO_EXPORT_DOWNLOAD,
} from '@/constants';

function formatDateInput(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function defaultLastMonthPeriod() {
    const today = new Date();
    const y = today.getFullYear();
    const monthIndex = today.getMonth();
    const firstOfLastMonth = new Date(y, monthIndex - 1, 1);
    const lastOfLastMonth = new Date(y, monthIndex, 0);
    return {
        period_start: formatDateInput(firstOfLastMonth),
        period_end: formatDateInput(lastOfLastMonth),
    };
}

export default {
    name: 'FinanceVatZeroExport',
    data() {
        const { period_start, period_end } = defaultLastMonthPeriod();
        return {
            period_start,
            period_end,
            unitOptions: [
                { title: 'Все', value: 'all' },
                { title: 'B2B', value: 'B2B' },
                { title: 'B2C', value: 'B2C' },
            ],
            unitFilter: 'all',
            loadingExcel: false,
            loadingXmlConvert: false,
            loadingXmlMerge: false,
            downloadLoading: { excel: false, xml: false, mergedXml: false },
            message: { text: '', success: false },
            mergeMessage: { text: '', success: false },
            exports: {
                excel: { path: null, filename: null },
                xml: { path: null, filename: null },
                mergedXml: { path: null, filename: null },
            },
            excelUploadFile: null,
            excelUploadFilename: '',
            xmlMergeFiles: [],
            xmlMergeHeaderSource: 0,
        };
    },
    watch: {
        unitFilter() {
            this.clearExcelExport();
        },
        period_start() {
            this.clearExcelExport();
        },
        period_end() {
            this.clearExcelExport();
        },
    },
    methods: {
        clearExcelExport() {
            this.exports.excel = { path: null, filename: null };
            if (!this.exports.xml.path) {
                this.message = { text: '', success: false };
            }
        },
        clearXmlExport() {
            this.exports.xml = { path: null, filename: null };
        },
        clearMergedXmlExport() {
            this.exports.mergedXml = { path: null, filename: null };
            this.mergeMessage = { text: '', success: false };
        },
        submitExcelExport() {
            this.clearExcelExport();
            this.loadingExcel = true;
            const payload = {
                period_start: this.period_start || null,
                period_end: this.period_end || null,
                unit: this.unitFilter,
            };
            api.call('post', API_FINANCE_VAT_ZERO_EXPORT, payload)
                .then(({ data }) => {
                    this.message = { text: data.message || 'Excel сформирован.', success: true };
                    this.exports.excel = {
                        path: data.path || null,
                        filename: data.filename || null,
                    };
                })
                .catch((err) => {
                    const msg = (err.response && err.response.data && (err.response.data.message || (err.response.data.errors && JSON.stringify(err.response.data.errors)))) || err.message || 'Ошибка запроса';
                    this.message = { text: msg, success: false };
                })
                .finally(() => {
                    this.loadingExcel = false;
                });
        },
        pickExcelFile() {
            this.$refs.excelFileInput.click();
        },
        onExcelFileSelected(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }
            this.excelUploadFile = file;
            this.excelUploadFilename = file.name;
            this.clearXmlExport();
            this.convertExcelToXml();
            event.target.value = '';
        },
        convertExcelToXml() {
            if (!this.excelUploadFile) {
                return;
            }
            this.clearXmlExport();
            this.loadingXmlConvert = true;
            const formData = new FormData();
            formData.append('file', this.excelUploadFile);

            axios
                .post(API_FINANCE_VAT_ZERO_EXPORT_CONVERT_XML, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(({ data }) => {
                    this.message = { text: data.message || 'XML сформирован.', success: true };
                    this.exports.xml = {
                        path: data.path || null,
                        filename: data.filename || null,
                    };
                })
                .catch((err) => {
                    const msg = (err.response && err.response.data && (err.response.data.message || (err.response.data.errors && JSON.stringify(err.response.data.errors)))) || err.message || 'Ошибка преобразования';
                    this.message = { text: msg, success: false };
                })
                .finally(() => {
                    this.loadingXmlConvert = false;
                });
        },
        pickXmlMergeFiles() {
            this.$refs.xmlMergeFileInput.click();
        },
        onXmlMergeFilesSelected(event) {
            const list = event.target.files;
            if (!list || list.length === 0) {
                return;
            }
            this.xmlMergeFiles = Array.from(list);
            if (this.xmlMergeHeaderSource >= this.xmlMergeFiles.length) {
                this.xmlMergeHeaderSource = 0;
            }
            this.clearMergedXmlExport();
            event.target.value = '';
        },
        mergeXmlFiles() {
            if (this.xmlMergeFiles.length < 2) {
                return;
            }
            this.clearMergedXmlExport();
            this.loadingXmlMerge = true;

            const formData = new FormData();
            this.xmlMergeFiles.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });
            formData.append('header_source', String(this.xmlMergeHeaderSource));

            axios
                .post(API_FINANCE_VAT_ZERO_EXPORT_MERGE_XML, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(({ data }) => {
                    this.mergeMessage = { text: data.message || 'XML объединён.', success: true };
                    this.exports.mergedXml = {
                        path: data.path || null,
                        filename: data.filename || null,
                    };
                })
                .catch((err) => {
                    const msg = (err.response && err.response.data && (err.response.data.message || (err.response.data.errors && JSON.stringify(err.response.data.errors)))) || err.message || 'Ошибка объединения';
                    this.mergeMessage = { text: msg, success: false };
                })
                .finally(() => {
                    this.loadingXmlMerge = false;
                });
        },
        downloadExport(format) {
            const exportInfo = this.exports[format];
            if (!exportInfo || !exportInfo.path) {
                return;
            }
            this.downloadLoading[format] = true;
            const mimeType = (format === 'xml' || format === 'mergedXml') ? 'application/xml' : 'application/vnd.ms-excel';
            const fallbackName = (format === 'xml' || format === 'mergedXml') ? 'vat-zero-avia.xml' : 'nds-zero-avia.xls';

            axios
                .get(API_FINANCE_VAT_ZERO_EXPORT_DOWNLOAD, {
                    params: { path: exportInfo.path },
                    responseType: 'blob',
                })
                .then((res) => {
                    const blob = new Blob([res.data], { type: mimeType });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', exportInfo.filename || fallbackName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
                .catch(() => {
                    this.message = { text: 'Не удалось скачать файл. Сформируйте выгрузку заново.', success: false };
                })
                .finally(() => {
                    this.downloadLoading[format] = false;
                });
        },
    },
};
</script>

<style scoped>
.vat-zero-rules {
    font-size: 0.72rem;
    line-height: 1.35;
    color: #5c636a;
}
.vat-zero-rules__title {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.vat-zero-rules ul li {
    margin-bottom: 0.15rem;
}
.vat-zero-download-wrap {
    max-width: 32rem;
    margin-left: auto;
    margin-right: auto;
}
.vat-zero-download-btn {
    max-width: 25rem;
    min-width: 12rem;
    height: 50px;
    font-weight: 600;
}
.vat-zero-convert {
    max-width: 40rem;
}
.vat-zero-merge-list {
    max-width: 36rem;
}
</style>
